import { create } from "zustand";
import toast from "react-hot-toast";
import { generateVideo, getVideoStatus } from "@/services/ads/videos/videosService";
import type { GenerateVideoRequest, VideoStatusResponse } from "@/services/ads/videos/videos.types";

// ── Backend overall status values ──
const VIDEO_OVERALL_STATUS = {
  FINISHED: "finished",
  ERROR: "error",
  PROCESSING: "processing",
  MERGING: "merging",
  WAITING_SCENES: "waiting_scenes",
} as const;

const STORAGE_KEY = "fluxi_video_generating_items";

export type VideoGeneratingItemStatus = "generating" | "done" | "error";

export interface VideoGeneratingItem {
  id: string;
  referenceId?: string;
  backendId?: number;
  status: VideoGeneratingItemStatus;
  videoUrl?: string;
  baseImageUrl?: string;
  format: string;
  productName: string;
  productImageUrl?: string;
  errorMessage?: string;
  totalScenes?: number;
  completedScenes?: number;
  overallStatus?: string;
}

interface VideoGenerationState {
  items: VideoGeneratingItem[];
  startGeneration: (
    request: GenerateVideoRequest,
    productName: string
  ) => Promise<boolean>;
  clearCompleted: () => void;
  clearAll: () => void;
  removeItems: (ids: string[]) => void;
}

let jobCounter = 0;
let pollingInterval: ReturnType<typeof setInterval> | null = null;

// Scene-based generation takes 8-15 minutes depending on scene count.
// We poll every 15s for up to 120 attempts = 30 min max.
const POLL_INTERVAL_MS = 15_000;
const MAX_POLL_ATTEMPTS = 120;

// ── localStorage persistence ──
function saveToStorage(items: VideoGeneratingItem[]) {
  try {
    const generating = items.filter((i) => i.status === "generating");
    if (generating.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(generating));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage not available
  }
}

function loadFromStorage(): VideoGeneratingItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const items: VideoGeneratingItem[] = JSON.parse(stored);
    return items.filter((i) => i.status === "generating" && i.referenceId);
  } catch {
    return [];
  }
}

function stopPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}

/** Update a single item based on the backend VideoStatusResponse. */
function reconcileItem(
  item: VideoGeneratingItem,
  status: VideoStatusResponse
): VideoGeneratingItem {
  switch (status.overall_status) {
    case VIDEO_OVERALL_STATUS.FINISHED:
      return {
        ...item,
        status: "done" as const,
        videoUrl: status.video_url || undefined,
        totalScenes: status.total_scenes,
        completedScenes: status.completed_scenes,
        overallStatus: status.overall_status,
      };
    case VIDEO_OVERALL_STATUS.ERROR: {
      const failedScenes = status.scenes?.filter(s => s.has_error).length ?? 0;
      const errorMsg = failedScenes > 0
        ? `Fallaron ${failedScenes} de ${status.total_scenes} escenas`
        : "La generación del video falló";
      return {
        ...item,
        status: "error" as const,
        errorMessage: errorMsg,
        totalScenes: status.total_scenes,
        completedScenes: status.completed_scenes,
        overallStatus: status.overall_status,
      };
    }
    case VIDEO_OVERALL_STATUS.PROCESSING:
    case VIDEO_OVERALL_STATUS.MERGING:
    case VIDEO_OVERALL_STATUS.WAITING_SCENES:
    default:
      return {
        ...item,
        totalScenes: status.total_scenes,
        completedScenes: status.completed_scenes,
        overallStatus: status.overall_status,
      };
  }
}

/** Mark all remaining generating items as timed-out errors. */
function timeoutItems(items: VideoGeneratingItem[]): VideoGeneratingItem[] {
  return items.map((i) =>
    i.status === "generating"
      ? { ...i, status: "error" as const, errorMessage: "Tiempo de espera agotado" }
      : i
  );
}

function startPolling() {
  if (pollingInterval) return;

  let attempts = 0;

  pollingInterval = setInterval(async () => {
    attempts++;
    const { items } = useVideoGenerationStore.getState();

    const generating = items.filter((i) => i.status === "generating");
    if (generating.length === 0) {
      stopPolling();
      saveToStorage([]);
      return;
    }

    if (attempts > MAX_POLL_ATTEMPTS) {
      stopPolling();
      const timedOut = timeoutItems(useVideoGenerationStore.getState().items);
      useVideoGenerationStore.setState({ items: timedOut });
      saveToStorage(timedOut);
      return;
    }

    // Skip fetch if no items have a referenceId yet
    const trackable = generating.filter((i) => i.referenceId);
    if (trackable.length === 0) return;

    // Poll each generating item individually by referenceId
    for (const item of trackable) {
      try {
        const statusResponse = await getVideoStatus(item.referenceId!);

        useVideoGenerationStore.setState((state) => {
          const updated = state.items.map((i) =>
            i.id === item.id ? reconcileItem(i, statusResponse) : i
          );
          saveToStorage(updated);
          return { items: updated };
        });
      } catch (err) {
        console.error(`[VideoGeneration] Polling error for ${item.referenceId}:`, err);
      }
    }

    // Check if all done
    const updated = useVideoGenerationStore.getState().items;
    if (!updated.some((i) => i.status === "generating")) {
      stopPolling();
      saveToStorage([]);
    }
  }, POLL_INTERVAL_MS);
}

// ── Restore from localStorage on load ──
const restoredItems = loadFromStorage();

const useVideoGenerationStore = create<VideoGenerationState>((set) => ({
  items: restoredItems,

  startGeneration: async (request, productName) => {
    try {
      const response = await generateVideo(request);

      const newItem: VideoGeneratingItem = {
        id: `vgen-${Date.now()}-${jobCounter++}`,
        referenceId: response.reference_id,
        status: "generating" as const,
        format: "9:16",
        productName,
        totalScenes: response.total_scenes,
        completedScenes: 0,
        overallStatus: response.status,
      };

      set((state) => {
        const updated = [newItem, ...state.items];
        saveToStorage(updated);
        return { items: updated };
      });

      startPolling();

      return true;
    } catch (error: unknown) {
      const err = error as {
        message?: string;
        response?: { data?: { message?: string; error?: string }; status?: number };
      };
      console.error("[VideoGeneration] Error:", err?.message);

      const backendMessage =
        err?.response?.data?.message || err?.response?.data?.error;
      toast.error(
        backendMessage ||
          "Error al iniciar la generación de video. Intenta de nuevo."
      );
      return false;
    }
  },

  clearCompleted: () => {
    set((state) => {
      const updated = state.items.filter((i) => i.status !== "done");
      saveToStorage(updated);
      return { items: updated };
    });
    const remaining = useVideoGenerationStore.getState().items;
    if (!remaining.some((i) => i.status === "generating")) {
      stopPolling();
    }
  },

  clearAll: () => {
    set({ items: [] });
    saveToStorage([]);
    stopPolling();
  },

  removeItems: (ids) => {
    set((state) => {
      const updated = state.items.filter((i) => !ids.includes(i.id));
      saveToStorage(updated);
      return { items: updated };
    });
  },
}));

// Resume polling if there are restored generating items
if (restoredItems.length > 0) {
  startPolling();
}

export default useVideoGenerationStore;
